import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { isOrderNumberForOrderId } from "./order-number.js";

const TransactionIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const MoneyFenSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const Sha256Schema = Type.String({ pattern: "^[a-f0-9]{64}$" });
const CanonicalDateTimeSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}\\.[0-9]{3}Z$",
});

export const OrderTransactionPaymentSnapshotSchema = Type.Object(
  {
    paymentId: TransactionIdSchema,
    method: Type.Union([
      Type.Literal("CASH"),
      Type.Literal("STATIC_WECHAT"),
      Type.Literal("STATIC_ALIPAY"),
      Type.Literal("MEMBER_BALANCE"),
      Type.Literal("WECHAT_PAY"),
      Type.Literal("ALIPAY"),
    ]),
    status: Type.Union([
      Type.Literal("CONFIRMED_LOCAL"),
      Type.Literal("PENDING_RECONCILIATION"),
      Type.Literal("MANUALLY_RECONCILED"),
      Type.Literal("RECONCILIATION_EXCEPTION"),
      Type.Literal("PARTIALLY_REFUNDED"),
      Type.Literal("REFUNDED"),
      Type.Literal("CONFIRMED_CLOUD"),
    ]),
    amountFen: MoneyFenSchema,
    refundedFen: MoneyFenSchema,
  },
  { additionalProperties: false },
);
export type OrderTransactionPaymentSnapshot = Static<typeof OrderTransactionPaymentSnapshotSchema>;

/**
 * The sole immutable transaction fact shape used to approve and later execute an order mutation.
 * Presence-only collections are deliberate in the first cash-refund slice: any non-empty collection
 * is a fail-closed boundary until its own reversal contract is delivered.
 */
export const OrderTransactionSnapshotSchema = Type.Object(
  {
    snapshotVersion: Type.Literal(1),
    tenantId: TransactionIdSchema,
    storeId: TransactionIdSchema,
    orderId: TransactionIdSchema,
    orderNumber: Type.String({ minLength: 1, maxLength: 80 }),
    businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
    tableNumber: Type.Union([Type.String({ minLength: 1, maxLength: 32 }), Type.Null()]),
    orderStatus: Type.Union([
      Type.Literal("OPEN"),
      Type.Literal("SETTLED"),
      Type.Literal("REFUNDED"),
      Type.Literal("CONFLICT"),
    ]),
    orderPaidFen: MoneyFenSchema,
    orderAlreadyRefundedFen: MoneyFenSchema,
    orderItemIds: Type.Array(TransactionIdSchema, { maxItems: 10_000, uniqueItems: true }),
    coachServiceIds: Type.Array(TransactionIdSchema, { maxItems: 10_000, uniqueItems: true }),
    groupBuyFulfillmentIds: Type.Array(TransactionIdSchema, {
      maxItems: 10_000,
      uniqueItems: true,
    }),
    refundIds: Type.Array(TransactionIdSchema, { maxItems: 10_000, uniqueItems: true }),
    payments: Type.Array(OrderTransactionPaymentSnapshotSchema, {
      minItems: 1,
      maxItems: 32,
    }),
    businessFactSha256: Sha256Schema,
    capturedAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type OrderTransactionSnapshot = Static<typeof OrderTransactionSnapshotSchema>;

function isCanonicalTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

function isSortedUnique(values: readonly string[]): boolean {
  return values.every((value, index) => {
    const previous = values[index - 1];
    return index === 0 || (previous !== undefined && previous < value);
  });
}

export function isOrderTransactionSnapshot(value: unknown): value is OrderTransactionSnapshot {
  if (!Value.Check(OrderTransactionSnapshotSchema, value)) return false;
  if (!isOrderNumberForOrderId(value.orderNumber, value.orderId, value.businessDate)) return false;
  if (!isCanonicalTimestamp(value.capturedAt)) return false;
  if (value.orderAlreadyRefundedFen > value.orderPaidFen) return false;
  if (
    !isSortedUnique(value.orderItemIds) ||
    !isSortedUnique(value.coachServiceIds) ||
    !isSortedUnique(value.groupBuyFulfillmentIds) ||
    !isSortedUnique(value.refundIds)
  ) {
    return false;
  }
  if (!isSortedUnique(value.payments.map((payment) => payment.paymentId))) return false;
  return value.payments.every((payment) => payment.refundedFen <= payment.amountFen);
}
